type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];

/**
 * Converts a JSON object to XML string with proper formatting
 * Arrays are handled with a "List" suffix in parent tags
 * @param json - The JSON object to convert
 * @param indent - Current indentation level (used recursively)
 * @returns Formatted XML string
 */
export function jsonToXml(json: JsonValue, indent: number = 0): string {
    // console.log('\x1b[43m%s\x1b[0m', 'jsonToXml', json)
    try {
        const spaces = "    ".repeat(indent);

        // Handle null or undefined
        if (json === null || json === undefined) {
            return `${spaces}<null/>`;
        }

        // Handle primitive types
        if (typeof json !== "object") {
            return `${spaces}${String(json)}`;
        }

        // Handle arrays
        if (Array.isArray(json)) {
            if (json.length === 0) return `${spaces}<empty/>`;
            
            // Get parent tag from call stack or default to "item"
            const parentTag = "item";
            return json.map(item => 
                `${spaces}<${parentTag}>\n${jsonToXml(item, indent + 1)}\n${spaces}</${parentTag}>`
            ).join("\n");
        }

        // Handle objects
        const xml = Object.entries(json).map(([key, value]) => {
            // Handle array values - add "List" suffix to parent tag
            if (Array.isArray(value)) {
                const singularTag = key.endsWith("s") ? key.slice(0, -1) : key;
                return `${spaces}<${key}List>\n${value.map(item =>
                    `${spaces}    <${singularTag}>${jsonToXml(item, indent + 2)}\n${spaces}    </${singularTag}>`
                ).join("\n")}\n${spaces}</${key}List>`;
            }

            // Handle nested objects
            if (value && typeof value === "object") {
                return `${spaces}<${key}>\n${jsonToXml(value, indent + 1)}\n${spaces}</${key}>`;
            }

            // Handle primitive values
            return `${spaces}<${key}>${escapeXml(String(value))}</${key}>`;
        }).join("\n");

        // console.log('\x1b[43m%s\x1b[0m', 'xml', xml)
        return xml
    } catch (error) {
        throw new Error(`Failed to convert JSON to XML: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Converts XML string back to JSON object
 * Reverses the jsonToXml conversion, handling arrays with "List" suffix
 * @param xml - The XML string to convert
 * @returns JSON object
 */
export function xmlToJson(xml: string): JsonValue {
    try {
        // Remove leading/trailing whitespace
        xml = xml.trim();

        // Handle empty/null cases
        if (xml === '<null/>' || xml === '<empty/>') {
            return null;
        }

        // If it's not wrapped in tags, it's a primitive value
        if (!xml.startsWith('<') || !xml.includes('>')) {
            // Try to parse as number, boolean, or return as string
            if (xml === 'true') return true;
            if (xml === 'false') return false;
            if (xml === 'null') return null;
            
            const num = Number(xml);
            if (!isNaN(num) && isFinite(num)) return num;
            
            return unescapeXml(xml);
        }

        // Parse XML tags
        const result: JsonObject = {};
        const tagRegex = /<(\w+)(?:List)?>[\s\S]*?<\/\1(?:List)?>/g;
        let match;

        while ((match = tagRegex.exec(xml)) !== null) {
            const fullTag = match[0];
            const tagName = match[1];
            
            // Extract content between tags
            const contentStart = fullTag.indexOf('>') + 1;
            const contentEnd = fullTag.lastIndexOf('</');
            const content = fullTag.substring(contentStart, contentEnd).trim();

            // Check if this is a List tag
            if (fullTag.includes(`<${tagName}List>`)) {
                // Parse array
                const items: JsonValue[] = [];
                const singularTag = tagName.endsWith('s') ? tagName.slice(0, -1) : tagName;
                const itemRegex = new RegExp(`<${singularTag}>[\\s\\S]*?<\\/${singularTag}>`, 'g');
                let itemMatch;

                while ((itemMatch = itemRegex.exec(content)) !== null) {
                    const itemContent = itemMatch[0]
                        .replace(new RegExp(`^<${singularTag}>`), '')
                        .replace(new RegExp(`<\\/${singularTag}>$`), '')
                        .trim();
                    items.push(xmlToJson(itemContent));
                }

                result[tagName] = items;
            } else {
                // Parse single value or nested object
                if (content.includes('<') && content.includes('>')) {
                    // Nested object
                    result[tagName] = xmlToJson(content);
                } else {
                    // Primitive value
                    result[tagName] = xmlToJson(content);
                }
            }
        }

        // If no tags were found but it starts with <, it might be a single tag with content
        if (Object.keys(result).length === 0 && xml.startsWith('<')) {
            const singleTagMatch = xml.match(/^<(\w+)>(.*)<\/\1>$/);
            if (singleTagMatch) {
                const content = singleTagMatch[2].trim();
                return xmlToJson(content);
            }
        }

        // Return single value if only one property, otherwise return object
        const keys = Object.keys(result);
        return keys.length === 1 ? result[keys[0]] : result;

    } catch (error) {
        console.error('Failed to convert XML to JSON:', error)
        console.log('xml', xml)
        throw new Error(`Failed to convert XML to JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Escapes special characters in XML content
 */
function escapeXml(unsafe: string): string {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

/**
 * Unescapes special characters in XML content
 */
function unescapeXml(safe: string): string {
    return safe
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'");
}